import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  List,
  ListItem,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete
} from "@mui/material";
import {
  AssignmentIndOutlined,
  AssignmentOutlined,
  AssignmentTurnedInOutlined,
  ForwardToInboxOutlined,
  MarkEmailReadOutlined,
  NoteAltOutlined
} from "@mui/icons-material";
import DialogTitle from "../../../shared/dialog/DialogTitle";
import { closeSignatureStatusDialog } from "../../../../business/actions/campaignDashboardActions";
import {
  downloadSignedAgreement,
  getDigitalSignatureAgreementStatus,
  updateAgreement
} from "../../../../business/models/Plan/planActions";
import { getPlan } from "../../../../business/models/Plan/planSelectors";
import { Typography } from "../styles/planExportDialog";
import {
  AGREEMENT_ACTION_COMPLETED,
  AGREEMENT_ACTION_REQUESTED,
  AGREEMENT_CREATED,
  AGREEMENT_DOCUMENTS_VIEWED,
  AGREEMENT_EMAIL_VIEWED,
  AGREEMENT_WORKFLOW_COMPLETED,
  EMAIL,
  SIGNER,
  COMMENT
} from "../../../../business/constants/SignatureStatusDialogConstants";
import { PIPE_SYMBOL } from "../../../../business/constants/PlanExportDialogConstants";
import { validation } from "../../../../business/models/common/utils/validationUtils";
import {
  classes,
  Button,
  Typography as ErrorMessageTypography
} from "../styles/signatureStatusDialog";

const SignatureStatusDialog = () => {
  const dispatch = useDispatch();
  const currentPlan = useSelector(getPlan);
  const [agreementStatus, setAgreementStatus] = useState(undefined);
  const [apiMessage, setApiMessage] = useState("");
  const [apiIsSuccessful, setApiIsSuccessful] = useState(true);
  const [signerDetail, setSignerDetail] = useState([]);
  const [rowErrorMessage, setRowErrorMessage] = useState([]);
  const initializeRowErrorMessage = length => {
    const errorRow = [];
    for (let i = 0; i < length; i += 1) {
      errorRow[i] = { row: i, error: "" };
    }
    setRowErrorMessage(errorRow);
  };

  useEffect(() => {
    const getAgreementStatus = async () => {
      const { agreementStatusInfo, message, isSuccessful } = await dispatch(
        getDigitalSignatureAgreementStatus(
          currentPlan.digitalSignatureAgreementId
        )
      );
      setAgreementStatus(agreementStatusInfo);
      setApiMessage(message);
      setApiIsSuccessful(isSuccessful);
      initializeRowErrorMessage(agreementStatusInfo?.length);
    };
    getAgreementStatus();
  }, [dispatch, currentPlan.digitalSignatureAgreementId]);

  const handleCancel = () => {
    dispatch(closeSignatureStatusDialog());
  };

  const handleButtonClick = agreement => {
    if (agreement.availableForDownload) {
      dispatch(
        downloadSignedAgreement(`${agreement.name}|${agreement.identifier}`)
      );
    } else {
      const selectedValue = signerDetail.find(
        x => x.id === agreement.identifier
      );
      dispatch(
        updateAgreement(
          `STATE|${agreement.identifier}|${
            selectedValue ? selectedValue.comment : ""
          }`
        )
      );
    }
  };

  const handleSignerControlOnChange = value => {
    const splittedValue = value.split(PIPE_SYMBOL);
    const attribute = splittedValue[0];
    const id = splittedValue[1];
    const email = attribute === EMAIL ? splittedValue[2] : "";
    const signer = attribute === SIGNER ? splittedValue[2] : "";
    const comment = attribute === COMMENT ? splittedValue[2] : "";

    if (signerDetail.some(x => x.id === id)) {
      let newSignerDetail;
      switch (attribute) {
        case EMAIL:
          newSignerDetail = signerDetail.map(x =>
            x.id === id ? { ...x, email } : x
          );
          break;
        case SIGNER:
          newSignerDetail = signerDetail.map(x =>
            x.id === id ? { ...x, signer } : x
          );
          break;
        case COMMENT:
          newSignerDetail = signerDetail.map(x =>
            x.id === id ? { ...x, comment } : x
          );
          break;
        default:
          newSignerDetail = signerDetail;
      }
      setSignerDetail(newSignerDetail);
    } else {
      setSignerDetail([...signerDetail, { id, email, signer, comment }]);
    }
  };

  const setErrorMessageInState = (rowNo, Message) => {
    setRowErrorMessage(
      rowErrorMessage.map(x => (x.row === rowNo ? { ...x, error: Message } : x))
    );
  };

  const handleUpdateSigner = (rowNo, identifier) => {
    setErrorMessageInState(rowNo, "");
    const selectedValue = signerDetail.find(x => x.id === identifier);

    if (!selectedValue) {
      setErrorMessageInState(rowNo, "Signer and Email not selected");
      return;
    }

    if (selectedValue.signer.length < 2) {
      setErrorMessageInState(rowNo, "Signer is not selected");
      return;
    }

    if (selectedValue.email.length < 2) {
      setErrorMessageInState(rowNo, "Email not provided");
      return;
    }

    if (!validation.email(selectedValue.email)) {
      setErrorMessageInState(rowNo, "Email is not valid");
      return;
    }

    dispatch(
      updateAgreement(
        `ALTERNATERECIPIENT|${selectedValue.id}|${selectedValue.signer}|${selectedValue.email}`
      )
    );
  };
  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle id="signature-status-dialog-title">
        <Typography component="span" variant="h6" color="secondary">
          Agreement/Document Signature Status
        </Typography>
      </DialogTitle>
      <DialogContent>
        {agreementStatus && agreementStatus.length < 1 && (
          <Grid item xs={12}>
            <Alert key={`alert-empty-records`} severity="warning">
              No records found
            </Alert>
          </Grid>
        )}
        {!apiIsSuccessful && (
          <Grid item xs={12}>
            <Alert key={`alert-api-error-message`} severity="error">
              {apiMessage}
            </Alert>
          </Grid>
        )}
        <Grid container>
          {agreementStatus &&
            agreementStatus.length > 0 &&
            agreementStatus.map((x, y) => {
              return (
                <Grid
                  key={`main-grid-item-${y}`}
                  item
                  alignContent="center"
                  xs={12}
                >
                  <Grid container>
                    <Grid xs={7} item alignContent="center">
                      <Typography variant="subtitle1" color="primary">
                        {`${x.name} (Requestor - ${x.createdBy})`}
                      </Typography>
                    </Grid>
                    <Grid xs={5} item alignContent="center">
                      <Grid container>
                        <Grid xs={12} item>
                          <FormControl id={`FormControl-${x.identifier}`}>
                            <RadioGroup
                              row
                              aria-labelledby={`FormControl-${x.identifier}`}
                              name="row-radio-buttons-group"
                              onChange={event =>
                                handleSignerControlOnChange(
                                  `${SIGNER}${PIPE_SYMBOL}${x.identifier}${PIPE_SYMBOL}${event.target.value}`
                                )
                              }
                            >
                              <FormControlLabel
                                value="Signer1"
                                control={<Radio />}
                                label="Signer1"
                              />
                              <FormControlLabel
                                value="Signer2"
                                control={<Radio />}
                                label="Signer2"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid xs={12} item>
                          <Autocomplete
                            id={`Autocomplete-${x.identifier}`}
                            name={`Autocomplete-${x.identifier}`}
                            autoHighlight
                            freeSolo
                            multiple
                            filterSelectedOptions
                            options={[]}
                            ChipProps={{ color: "primary" }}
                            clearOnBlur
                            renderInput={params => {
                              return (
                                <>
                                  <TextField
                                    {...params}
                                    id={`TextField-${x.identifier}`}
                                    name={`TextField-${x.identifier}`}
                                    label="Email Address* (Type the email address and press enter...)"
                                    variant="outlined"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment:
                                        params.InputProps.endAdornment,
                                      maxLength: 100
                                    }}
                                  />
                                </>
                              );
                            }}
                            onChange={(_, data, reason, detail) => {
                              const email =
                                reason === "removeOption" ? "" : detail.option;
                              handleSignerControlOnChange(
                                `${EMAIL}${PIPE_SYMBOL}${x.identifier}${PIPE_SYMBOL}${email}`
                              );
                            }}
                          />
                        </Grid>
                        <Grid xs={12} item paddingTop={1}>
                          <TextField
                            id={`Comment-${x.identifier}`}
                            name={`Comment-${x.identifier}`}
                            label="Cancellation Comment (Optional)"
                            variant="outlined"
                            fullWidth
                            onChange={event => {
                              handleSignerControlOnChange(
                                `${COMMENT}${PIPE_SYMBOL}${x.identifier}${PIPE_SYMBOL}${event.target.value}`
                              );
                            }}
                          />
                        </Grid>
                        <Grid xs={12} item>
                          <Grid container paddingTop={1} paddingBottom={1}>
                            <Grid item xs={6}>
                              <Button
                                key={`updatesigner-button-${y}`}
                                type="contained"
                                className={classes.signerButton}
                                onClick={() => {
                                  handleUpdateSigner(y, x.identifier);
                                }}
                                padding="1"
                              >
                                Update Signer
                              </Button>
                            </Grid>
                            <Grid
                              item
                              container
                              xs={6}
                              justifyContent="flex-end"
                            >
                              <Button
                                key={`download-button-${y}`}
                                type="contained"
                                className={classes.cancelButton}
                                onClick={() => {
                                  handleButtonClick(x);
                                }}
                              >
                                {x.availableForDownload
                                  ? "Download Agreement"
                                  : "Cancel Agreement"}
                              </Button>
                            </Grid>
                          </Grid>
                          <Grid>
                            {rowErrorMessage[y]?.error.length > 0 && (
                              <ErrorMessageTypography
                                className={classes.errorMessageTypography}
                              >
                                {rowErrorMessage[y]?.error}
                              </ErrorMessageTypography>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Divider></Divider>
                  {x.statusInfo.map((status, index) => {
                    return (
                      <Grid container key={`child-grid-container-${index}`}>
                        <Grid item xs={12}>
                          <List>
                            <ListItem>
                              <Grid container>
                                <Grid xs={0.4} item>
                                  {(() => {
                                    switch (status.event) {
                                      case AGREEMENT_CREATED:
                                        return (
                                          <AssignmentOutlined></AssignmentOutlined>
                                        );
                                      case AGREEMENT_ACTION_REQUESTED:
                                        return (
                                          <ForwardToInboxOutlined></ForwardToInboxOutlined>
                                        );
                                      case AGREEMENT_EMAIL_VIEWED:
                                        return (
                                          <MarkEmailReadOutlined></MarkEmailReadOutlined>
                                        );
                                      case AGREEMENT_DOCUMENTS_VIEWED:
                                        return (
                                          <AssignmentIndOutlined></AssignmentIndOutlined>
                                        );
                                      case AGREEMENT_ACTION_COMPLETED:
                                        return (
                                          <NoteAltOutlined></NoteAltOutlined>
                                        );
                                      case AGREEMENT_WORKFLOW_COMPLETED:
                                        return (
                                          <AssignmentTurnedInOutlined></AssignmentTurnedInOutlined>
                                        );
                                      default:
                                        return (
                                          <AssignmentOutlined></AssignmentOutlined>
                                        );
                                    }
                                  })()}
                                </Grid>
                                <Grid xs={11.6} item>
                                  <Typography>
                                    {status.statusDescription}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </ListItem>
                          </List>
                        </Grid>
                      </Grid>
                    );
                  })}
                  <Divider></Divider>
                </Grid>
              );
            })}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignatureStatusDialog;
